import React, { useState, useMemo, useRef, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Button } from '@t-one-internal/design-system'
import { cn } from '@t-one-internal/design-system'
import { CaretRight } from '@phosphor-icons/react'
import { useSubscriptionStore } from '../stores/subscriptionStore'
import { useLearnSettingsStore } from '../stores/learnSettingsStore'
import { NAVIGATION_CONFIG, ALWAYS_VISIBLE_MODULES, TOP_LEVEL_NAV_ITEMS } from '../config/navigation'
import type { NavItemConfig } from '../config/navigation'

import { useAuth } from '../context/AuthContextBackend'
import { getUserRoleFromToken } from '../utils/jwt'
import { useUserAccess } from '../hooks/useUserAccess'
import { useAccountLogo } from '../hooks/useAccountLogo'



interface LogoProps {
  accountName?: string
  isAuthenticated: boolean
  isCollapsed: boolean
}

const Logo: React.FC<LogoProps> = ({ accountName, isAuthenticated, isCollapsed }) => {
  const { logoUrl } = useAccountLogo(isAuthenticated)
  const displayName = accountName || 'Memric'
  const [logoError, setLogoError] = useState(false)
  const [isSquare, setIsSquare] = useState<boolean | null>(null)

  const hasValidLogo = logoUrl && !logoError

  // Check if logo is square or rectangle
  useEffect(() => {
    if (!logoUrl || logoError) {
      setIsSquare(null)
      return
    }

    const img = new Image()
    img.onload = () => {
      const aspectRatio = img.width / img.height
      // Consider it square if aspect ratio is between 0.9 and 1.1 (allowing small variations)
      setIsSquare(aspectRatio >= 0.9 && aspectRatio <= 1.1)
    }
    img.onerror = () => {
      setLogoError(true)
      setIsSquare(null)
    }
    img.src = logoUrl
  }, [logoUrl, logoError])

  // Collapsed state - show only first letter or small logo
  if (isCollapsed) {
    if (hasValidLogo && isSquare === true) {
      return (
        <img
          src={logoUrl!}
          alt="Company Logo"
          className="w-8 h-8 rounded-lg object-cover"
          onError={() => {
            setLogoError(true)
          }}
        />
      )
    }
    return (
      <div
        className="flex items-center justify-center w-8 h-8 rounded-lg text-white font-bold text-sm"
        style={{ backgroundColor: '#4B45E5' }}
      >
        {displayName[0]?.toUpperCase() || 'M'}
      </div>
    )
  }

  // If no logo, show only text
  if (!hasValidLogo) {
    return (
      <span
        className="text-xl font-semibold text-gray-900"
        style={{
          lineHeight: '1.3',
          wordBreak: 'break-word',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          flex: 1,
        }}
      >
        {displayName}
      </span>
    )
  }

  // If square logo, show logo + text side by side
  if (isSquare === true) {
    return (
      <>
        <img
          src={logoUrl!}
          alt="Company Logo"
          className="w-8 h-8 rounded-lg object-cover flex-shrink-0"
          onError={() => {
            setLogoError(true)
          }}
        />
        <span
          className="text-xl font-semibold text-gray-900"
          style={{
            lineHeight: '1.3',
            wordBreak: 'break-word',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            flex: 1,
          }}
        >
          {displayName}
        </span>
      </>
    )
  }

  // If rectangle logo, show full width centered logo (no text)
  if (isSquare === false) {
    return (
      <img
        src={logoUrl!}
        alt="Company Logo"
        className="w-full h-auto rounded-lg object-contain"
        style={{ maxHeight: '60px' }}
        onError={() => {
          setLogoError(true)
        }}
      />
    )
  }

  // Loading state - show text while checking dimensions
  return (
    <span
      className="text-xl font-semibold text-gray-900"
      style={{
        lineHeight: '1.3',
        wordBreak: 'break-word',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        flex: 1,
      }}
    >
      {displayName}
    </span>
  )
}

interface CollapsibleSectionProps {
  title: string
  isCollapsed: boolean
  onToggle: () => void
  children: React.ReactNode
  sidebarCollapsed: boolean
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  isCollapsed,
  onToggle,
  children,
  sidebarCollapsed,
}) => {
  // If title is empty, render as a flat list of items (section-less)
  if (!title) {
    return (
      <div style={{
        padding: sidebarCollapsed ? '0 8px' : '0 16px',
        marginBottom: sidebarCollapsed ? '8px' : '24px'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {children}
        </div>
      </div>
    )
  }

  if (sidebarCollapsed) {
    // When sidebar is collapsed, show children directly without section title
    return (
      <div style={{ padding: '0 8px', marginBottom: '8px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {children}
        </div>
      </div>
    )
  }

  return (
    <div style={{ padding: '0 16px', marginBottom: '24px' }}>
      <button
        onClick={onToggle}
        className="flex items-center justify-between w-full transition-colors"
        style={{
          fontSize: '12px',
          fontWeight: '600',
          color: '#8B5CF6',
          letterSpacing: '0.05em',
          marginBottom: '12px',
          padding: '8px 0',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}
      >
        <span>{title}</span>
        <CaretRight
          className={cn(
            'transition-transform duration-200',
            isCollapsed ? 'rotate-0' : 'rotate-90'
          )}
          style={{ width: '12px', height: '12px' }}
        />
      </button>
      <div
        className={cn(
          'overflow-hidden transition-all duration-200',
          isCollapsed ? 'max-h-0' : 'max-h-96'
        )}
        style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}
      >
        {children}
      </div>
    </div>
  )
}

interface NavItemProps {
  icon: ((isActive: boolean) => React.ReactNode) | React.ReactNode
  label: string
  path: string
  activePath?: string
  excludeActivePath?: string
  isActive?: boolean
  onClick?: () => void
  children?: NavItemConfig[]
  isNested?: boolean
  sidebarCollapsed?: boolean
  isSubscriptionExpired?: boolean
  isSuperAdmin?: boolean
}

const NavItem: React.FC<NavItemProps> = ({
  icon,
  label,
  path,
  activePath,
  excludeActivePath,
  isActive = false,
  onClick,
  children,
  isNested = false,
  sidebarCollapsed = false,
  isSubscriptionExpired = false,
  isSuperAdmin: isSuperAdminProp = false,
}) => {
  const location = useLocation()
  const [isExpanded, setIsExpanded] = useState(false)
  const isExcluded = !!(excludeActivePath && location.pathname.startsWith(excludeActivePath))
  const active = !isExcluded && (isActive || location.pathname.startsWith(activePath || path))
  const userRole = getUserRoleFromToken()
  const [showTooltip, setShowTooltip] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 })
  const timeoutRef = useRef<number | null>(null)
  const buttonRef = useRef<HTMLDivElement>(null)

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = window.setTimeout(() => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect()
        setTooltipPosition({
          top: rect.top + rect.height / 2,
          left: rect.right + 12,
        })
      }
      setShowTooltip(true)
      // Trigger animation after a frame to ensure smooth transition
      requestAnimationFrame(() => {
        setIsAnimating(true)
      })
    }, 300)
  }

  const handleMouseLeave = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    setIsAnimating(false)
    setShowTooltip(false)
  }

  // Debug logging - MUST be before any conditional returns (React hooks rules)
  useEffect(() => {
    if (children && children.length > 0) {
      console.log(`🔍 NavItem "${label}" has ${children.length} children, userRole:`, userRole)
    }
  }, [children, label, userRole])

  // Filter children by role - MUST be before any conditional returns (React hooks rules)
  const filteredChildren = useMemo(() => {
    if (!children) return []

    const filtered = children.filter((child) => {
      // Check superadmin-only items
      if (child.requireSuperAdmin && !isSuperAdminProp) {
        console.log(`❌ Hiding "${child.label}" - superadmin required`);
        return false;
      }

      // Show items with no role requirement or 'any'
      if (!child.requiresRole || child.requiresRole === 'any') {
        return true;
      }

      // If user has no role yet, don't show role-specific items
      if (!userRole) {
        console.log(`⚠️ Hiding "${child.label}" - user role not loaded yet`);
        return false;
      }

      // Check if user's role matches required role
      const matches = child.requiresRole === userRole;
      console.log(`${matches ? '✅' : '❌'} "${child.label}" - required: ${child.requiresRole}, user: ${userRole}`);
      return matches;
    })

    console.log(`🔍 NavItem "${label}" filtered children:`, filtered.length, 'out of', children.length)
    return filtered
  }, [children, userRole, label, isSuperAdminProp])

  // Auto-expand if any child is active - MUST be before any conditional returns (React hooks rules)
  useEffect(() => {
    if (children && children.some(child => location.pathname.startsWith(child.path))) {
      setIsExpanded(true)
    }
  }, [location.pathname, children])

  // Collapsed sidebar view
  if (sidebarCollapsed) {
    return (
      <div className="relative group" ref={buttonRef}>
        <Button
          asChild
          variant="ghost"
          className={cn(
            'w-full justify-center font-normal relative transition-colors duration-200',
            isSubscriptionExpired
              ? 'text-gray-400 cursor-not-allowed'
              : active ? 'font-medium' : 'text-gray-700 hover:text-[#4B45E5] hover:bg-[#4B45E51A]'
          )}
          style={{
            height: '40px',
            padding: '0 8px',
            fontSize: '14px',
            lineHeight: '1.4',
            backgroundColor: active && !isSubscriptionExpired ? '#4B45E51A' : undefined,
            color: isSubscriptionExpired ? '#9CA3AF' : active ? '#4B45E5' : undefined,
            opacity: isSubscriptionExpired ? 0.5 : 1,
          }}
          onClick={onClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <Link
            to={path}
            className="flex items-center justify-center"
            style={{ textDecoration: 'none', width: '100%' }}
          >
            <div
              className="flex-shrink-0 flex items-center justify-center"
              style={{
                width: '20px',
                height: '20px',
                fontSize: '20px',
                lineHeight: '1',
              }}
            >
              {typeof icon === 'function' ? icon(isSubscriptionExpired ? false : active) : icon}
            </div>
          </Link>
        </Button>
        {showTooltip && (
          <div
            className="fixed px-3 py-2 text-sm font-medium text-white bg-gray-900 rounded-md shadow-xl whitespace-nowrap pointer-events-none"
            style={{
              fontFamily: 'system-ui, -apple-system, sans-serif',
              zIndex: 9999,
              left: `${tooltipPosition.left}px`,
              top: `${tooltipPosition.top}px`,
              transform: `translateY(-50%) translateX(${isAnimating ? '0' : '-8px'})`,
              opacity: isAnimating ? 1 : 0,
              transition: 'opacity 200ms ease-out, transform 200ms ease-out',
            }}
          >
            {label}{isSubscriptionExpired ? ' (Expired)' : ''}
            <div
              className="absolute right-full top-1/2 -translate-y-1/2 w-0 h-0"
              style={{
                borderTop: '5px solid transparent',
                borderBottom: '5px solid transparent',
                borderRight: '5px solid #111827',
                marginRight: '0px',
              }}
            />
          </div>
        )}
      </div>
    )
  }

  // Expanded sidebar with children
  if (children && filteredChildren.length > 0) {
    // Parent item with children
    return (
      <div style={{ opacity: isSubscriptionExpired ? 0.5 : 1 }}>
        <Button
          asChild
          variant="ghost"
          className={cn(
            'w-full justify-start font-normal transition-colors duration-200',
            isSubscriptionExpired
              ? 'text-gray-400 cursor-not-allowed'
              : active ? 'font-medium' : 'text-gray-700 hover:text-[#4B45E5] hover:bg-[#4B45E51A]'
          )}
          style={{
            height: '40px',
            padding: '0 16px',
            fontSize: '14px',
            lineHeight: '1.4',
            backgroundColor: active && !isSubscriptionExpired ? '#4B45E51A' : undefined,
            color: isSubscriptionExpired ? '#9CA3AF' : active ? '#4B45E5' : undefined,
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-3" style={{ cursor: 'pointer' }}>
            <div
              className="flex-shrink-0 flex items-center justify-center"
              style={{
                width: '20px',
                height: '20px',
                fontSize: '20px',
                lineHeight: '1',
              }}
            >
              {typeof icon === 'function' ? icon(false) : icon}
            </div>
            <span
              style={{
                fontSize: '14px',
                fontWeight: '600',
                lineHeight: '1.4',
                color: 'currentColor',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                flex: 1,
              }}
            >
              {label}
            </span>
            <CaretRight
              className={cn(
                'transition-transform duration-200',
                isExpanded ? 'rotate-90' : 'rotate-0'
              )}
              style={{ width: '14px', height: '14px', color: '#6B7280' }}
            />
          </div>
        </Button>
        {isExpanded && (
          <div style={{ paddingLeft: '28px', marginTop: '2px' }}>
            {filteredChildren.map((child) => (
              <NavItem
                key={child.id}
                icon={child.icon}
                label={child.label}
                path={child.path}
                activePath={child.activePath}
                excludeActivePath={child.excludeActivePath}
                isNested={true}
                sidebarCollapsed={sidebarCollapsed}
                isSubscriptionExpired={isSubscriptionExpired}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  // Regular nav item (leaf node)
  return (
    <Button
      asChild
      variant="ghost"
      className={cn(
        'w-full justify-start font-normal transition-colors duration-200',
        isSubscriptionExpired
          ? 'text-gray-400 cursor-not-allowed'
          : active ? 'font-medium' : 'text-gray-700 hover:text-[#4B45E5] hover:bg-[#4B45E51A]'
      )}
      style={{
        height: isNested ? '36px' : '40px',
        padding: '0 16px',
        fontSize: isNested ? '13px' : '14px',
        lineHeight: '1.4',
        backgroundColor: active && !isSubscriptionExpired ? '#4B45E51A' : undefined,
        color: isSubscriptionExpired ? '#9CA3AF' : active ? '#4B45E5' : undefined,
        opacity: isSubscriptionExpired ? 0.5 : 1,
      }}
      onClick={onClick}
    >
      <Link
        to={path}
        className="flex items-center gap-3"
        style={{ textDecoration: 'none' }}
      >
        <div
          className="flex-shrink-0 flex items-center justify-center"
          style={{
            width: isNested ? '16px' : '20px',
            height: isNested ? '16px' : '20px',
            fontSize: isNested ? '16px' : '20px',
            lineHeight: '1',
          }}
        >
          {typeof icon === 'function' ? icon(isSubscriptionExpired ? false : active) : icon}
        </div>
        <span
          style={{
            fontSize: isNested ? '13px' : '14px',
            fontWeight: '600',
            lineHeight: '1.4',
            color: 'currentColor',
            fontFamily: 'system-ui, -apple-system, sans-serif',
          }}
        >
          {label}
        </span>
      </Link>
    </Button>
  )
}

// Skeleton component for loading state
const SidebarSkeleton: React.FC<{ isCollapsed: boolean }> = ({ isCollapsed }) => {
  return (
    <div
      className="flex flex-col h-full border-r z-[60] transition-all duration-300 ease-in-out flex-shrink-0"
      style={{
        width: isCollapsed ? '64px' : '256px',
        background: 'linear-gradient(180deg, #EEF2FF 0%, #FAF5FF 50%, #FDF2F8 100%)',
        borderColor: '#E5E7EB',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      {/* Header skeleton */}
      <div className={cn(
        "flex items-center border-b border-gray-100",
        isCollapsed ? "justify-center px-2 py-4 flex-col gap-3" : "justify-between px-4 py-6"
      )}>
        <div className={cn(
          "flex items-center min-w-0",
          isCollapsed ? "justify-center" : "gap-3 flex-1"
        )}>
          <div
            className="animate-pulse rounded-lg bg-gray-200"
            style={{ width: isCollapsed ? '32px' : '120px', height: '32px' }}
          />
        </div>
      </div>

      {/* Navigation skeleton */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="animate-pulse rounded-md bg-gray-200"
              style={{
                height: '40px',
                width: isCollapsed ? '40px' : '100%',
                marginLeft: isCollapsed ? 'auto' : '0',
                marginRight: isCollapsed ? 'auto' : '0',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

interface DynamicSidebarProps {
  onNavigate?: (path: string) => void
  onCollapseChange?: (isCollapsed: boolean) => void
}

export const DynamicSidebar: React.FC<DynamicSidebarProps> = ({ onNavigate, onCollapseChange }) => {
  const { isSubmoduleEnabled, isModuleEnabled, isModuleActive, isLoading: isSubscriptionLoading } = useSubscriptionStore()
  const { user, account, updateAccount, isLoading: isAuthLoading } = useAuth()
  const { isSuperAdmin, isHRBP, isExecutive, isManager, isEmployee, hasModuleAccess, hasAccess, isLoading: isAccessLoading, isCustomerAdmin, hasCustomerModuleAccess, customerAccess, canManageCustomers, isCoach, isCoachee } = useUserAccess()
  const { marketplaceEnabled, learnerCourseAccess, isMarketplaceSidebarVisible, isCoursesSidebarVisible, fetchLearnSettings, refetchLearnSettings, isLoaded: isLearnSettingsLoaded } = useLearnSettingsStore()

  // Fetch learn settings when authenticated and Learn module is subscribed
  // isSubscriptionLoading is needed so the effect re-fires after subscriptions load
  useEffect(() => {
    if (!isAuthLoading && !isSubscriptionLoading && user && isModuleEnabled('learn')) {
      fetchLearnSettings()
    }
  }, [isAuthLoading, isSubscriptionLoading, user, isModuleEnabled, fetchLearnSettings])

  // Listen for customer label updates from workforce Feature Settings
  React.useEffect(() => {
    const handleLabelUpdate = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (detail?.customer_label) {
        updateAccount({ customer_label: detail.customer_label })
      }
    }
    window.addEventListener('customerLabelUpdated', handleLabelUpdate)
    return () => window.removeEventListener('customerLabelUpdated', handleLabelUpdate)
  }, [updateAccount])

  // Listen for learn settings updates from workforce Feature Settings
  React.useEffect(() => {
    const handleLearnSettingsUpdate = () => {
      refetchLearnSettings()
    }
    window.addEventListener('learnSettingsUpdated', handleLearnSettingsUpdate)
    return () => window.removeEventListener('learnSettingsUpdated', handleLearnSettingsUpdate)
  }, [refetchLearnSettings])

  // Debug logging for customer module access
  console.log('🔍 DynamicSidebar Debug:', {
    isCustomerAdmin,
    customerAccess,
    userAccess: (user as any)?.access,
    accountAccess: account?.access,
    hasCoachingAccess: hasCustomerModuleAccess('Coaching'),
  })
  const location = useLocation()
  const isAuthenticated = !!user
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({})

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    // Load from localStorage
    const saved = localStorage.getItem('sidebarCollapsed')
    return saved === 'true'
  })


  // Save sidebar collapsed state to localStorage and notify parent
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', String(isSidebarCollapsed))
    if (onCollapseChange) {
      onCollapseChange(isSidebarCollapsed)
    }
  }, [isSidebarCollapsed, onCollapseChange])


  const handleNavigate = (path: string) => {
    if (onNavigate) {
      onNavigate(path)
    }
  }

  // Get account name or domain
  const getAccountInfo = () => {
    if (!account) return 'No Organization'

    // API may return company_name instead of name
    const name = account.name || (account as any).company_name || (account as any).account_name
    if (name) {
      return name
    } else if (account.domain) {
      return account.domain
    }

    if (rawAccount.account_domain) {
      return rawAccount.account_domain
    }

    return 'Organization'
  }

  const toggleSection = (sectionId: string) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }))
  }

  // Filter navigation based on subscriptions, user roles, AND access matrix
  const filteredNavigation = useMemo(() => {
    // Detect external collaborator context:
    // - Collaborator landing page
    // - Job Builder edit route with collaborator token in query string
    const searchParams = new URLSearchParams(location.search)
    const hasCollaboratorToken = !!searchParams.get('token')
    const hasCollaboratorMode = localStorage.getItem('collaborator_mode') === 'true'
    const isCollaboratorRoute =
      location.pathname.includes('/comp-builder/job-builder/collaborator/') ||
      (location.pathname.includes('/comp-builder/job-builder/edit/') && (hasCollaboratorToken || hasCollaboratorMode)) ||
      (location.pathname.includes('/comp-builder/job-builder/titles') && hasCollaboratorMode)

    // Debug logging for collaborator detection
    if (location.pathname.includes('/comp-builder/job-builder/')) {
      console.log('🔧 [COLLABORATOR DETECTION]', {
        pathname: location.pathname,
        hasCollaboratorToken,
        hasCollaboratorMode,
        isCollaboratorRoute
      })
    }

    const baseNavigation = NAVIGATION_CONFIG.map((section) => {
      // Filter items based on subscription, role, AND access matrix
      const filteredItems = section.items.filter((item) => {
        // First, check subscription access (skip for always visible modules)
        // Note: Superadmin still respects subscription limits
        // ALSO skip subscription check for customer admins with module access - they use account.access[] not subscription
        // Portal users can't fetch subscriptions (no /private/v1 access), so skip for them too
        const skipSubscriptionCheck = ALWAYS_VISIBLE_MODULES.includes(section.moduleId) ||
          (item.customerModuleAccess && hasCustomerModuleAccess(item.customerModuleAccess))

        if (!skipSubscriptionCheck) {
          const moduleActive = isModuleActive(section.moduleId)
          if (item.submoduleId) {
            // Item has specific submodule - check if enabled
            if (!isSubmoduleEnabled(section.moduleId, item.submoduleId)) {
              console.log(`❌ Submodule disabled for: ${item.label} (module: ${section.moduleId}, submodule: ${item.submoduleId})`)
              return false
            }
          } else {
            // Item has no submoduleId - check if module has ANY enabled submodule
            if (!isModuleEnabled(section.moduleId)) {
              console.log(`❌ Module disabled for: ${item.label} (module: ${section.moduleId})`)
              return false
            }
          }
          // If is_active is false, hide the module from the sidebar entirely
          if (!moduleActive) {
            console.log(`❌ Module subscription expired, hiding: ${item.label} (module: ${section.moduleId})`)
            return false
          }
        }

        // Cross-module subscription gate — applies even within ALWAYS_VISIBLE sections
        // Superadmin does NOT bypass this (consistent with existing subscription behavior)
        if (item.requireModuleEnabled && !isModuleEnabled(item.requireModuleEnabled)) {
          return false
        }

        // Cross-module ANY-of subscription gate — show if ANY listed module is enabled AND active
        if (item.requireAnyModuleEnabled && !item.requireAnyModuleEnabled.some(m => isModuleEnabled(m) && isModuleActive(m))) {
          return false
        }

        // Cross-module submodule-level subscription gate — applies even within ALWAYS_VISIBLE sections
        if (item.requireSubmoduleEnabled && !isSubmoduleEnabled(item.requireSubmoduleEnabled.moduleId, item.requireSubmoduleEnabled.submoduleId)) {
          return false
        }

        // HIDE FROM CUSTOMER EMPLOYEES - Items that customer employees should never see
        // These are full module pages (Coaching, Training Management) that customer employees shouldn't access
        // Customer employees should use My Engagements, My Events instead

        // Feature flag: can_manage_customers must be enabled on the account
        if (item.requireCanManageCustomers && !canManageCustomers) {
          console.log(`❌ Customer management disabled for: ${item.label}`)
          return false
        }

        // Custom marketplace visibility
        // marketplace_enabled is the master switch — when off, hide for everyone
        if (item.id === 'marketplace') {
          if (!marketplaceEnabled) return false
          // Admins see it whenever marketplace is enabled (manage requests/bundles)
          const isLMSAdmin = isSuperAdmin ||
            ['COURSES_CREATE', 'COURSES_EDIT'].some(p => {
              try { return hasAccess(p as any) } catch { return false }
            })
          if (isLMSAdmin) return true
          // Learners: also need learner_course_access to be active
          return isMarketplaceSidebarVisible()
        }

        // Custom courses sidebar visibility for learners
        if (item.id === 'courses') {
          // Admin permissions: always show Courses
          const hasAdminCourseAccess = ['COURSES_CREATE', 'COURSES_EDIT'].some(p => {
            try { return hasAccess(p as any) } catch { return false }
          })
          if (hasAdminCourseAccess) return true
          // Learner-level: only show when learnerCourseAccess is PURCHASED_CATALOG or BOTH
          const coursesVisible = isCoursesSidebarVisible()
          console.log(`🔍 Courses sidebar for learner: learnerCourseAccess=${learnerCourseAccess}, isLoaded=${isLearnSettingsLoaded}, visible=${coursesVisible}`)
          return coursesVisible
        }

        // Check customer employee specific items FIRST (My Courses, My Engagements, My Events)
        // These are for customer employees AND customer admins (who are also customer employees with admin privileges)
        if (item.requireCustomerEmployee) {
          // Must be a customer employee OR customer admin to see these items
          // Customer admins are customer employees with additional admin privileges - they get BOTH admin views AND personal views
          if (!isCustomerEmployee && !isCustomerAdmin) {
            console.log(`❌ Access denied - not a customer employee for: ${item.label}`)
            return false
          }
          // Also check customer module access if specified
          if (item.customerModuleAccess && !hasCustomerModuleAccess(item.customerModuleAccess)) {
            console.log(`❌ Customer module access denied for: ${item.label} (requires ${item.customerModuleAccess})`)
            return false
          }
          console.log(`✅ Access granted for customer employee/admin: ${item.label}`)
          return true
        }

        // SUPERADMIN BYPASS - skip permission/role checks (but subscription is already checked above)
        // Note: This is AFTER requireCustomerEmployee check, so superadmins don't see customer-only pages
        if (isSuperAdmin) {
          console.log(`✅ SUPERADMIN bypass for: ${item.label}`)
          return true
        }

        // Check superadmin-only items - deny if not superadmin
        if (item.requireSuperAdmin) {
          console.log(`❌ Access denied - superadmin required for: ${item.label}`)
          return false
        }

        // CUSTOMER ADMIN CHECK - Must happen BEFORE permission checks
        // Customer admins with module access get access to Learn module pages (Courses, Coaching, TMS)
        if (item.customerModuleAccess && isCustomerAdmin) {
          if (hasCustomerModuleAccess(item.customerModuleAccess)) {
            console.log(`✅ Customer admin access granted for: ${item.label} (has ${item.customerModuleAccess} access)`)
            return true
          } else {
            console.log(`❌ Customer admin denied - no ${item.customerModuleAccess} access for: ${item.label}`)
            return false
          }
        }

        // STRICT RBAC - Check permissions first (for org employees, not customer employees)
        // If requireAnyPermission is specified, it's the PRIMARY access control
        if (item.requireAnyPermission && item.requireAnyPermission.length > 0) {
          const hasAnyPermission = item.requireAnyPermission.some(permission => {
            try {
              return hasAccess(permission as any)
            } catch {
              return false
            }
          })

          if (hasAnyPermission) {
            console.log(`✅ Access granted via permission for: ${item.label}`)
            return true
          } else {
            // STRICT: If permissions are required and user doesn't have them, DENY
            console.log(`❌ Access denied - missing required permission for: ${item.label}`)
            return false
          }
        }

        // Check if user has access via access matrix (this grants access regardless of role)
        // Use submoduleId (e.g., "cycle-management") to check matrix access
        const hasMatrixAccess = item.submoduleId ? hasModuleAccess(item.submoduleId) : false

        if (hasMatrixAccess) {
          console.log(`✅ Access granted via matrix for: ${item.label} (${item.submoduleId})`)
          return true // Access granted via matrix - skip role checks
        }

        // Otherwise, fall back to role-based checks
        // Use OR logic: if ANY role requirement is met, grant access
        let hasRoleAccess = false

        if (item.requireHRManager && isHRBP) {
          hasRoleAccess = true
        }
        if (item.requireRCManager && isManager) {
          hasRoleAccess = true
        }
        if (item.requireRCEmployee && isEmployee) {
          hasRoleAccess = true
        }
        if (item.requireAnyManager && (isHRBP || isManager)) {
          hasRoleAccess = true
        }
        if (item.requireCoach && (isCoach || isCoachee)) {
          hasRoleAccess = true
        }

        // Check customer module access
        if (item.customerModuleAccess && !hasCustomerModuleAccess(item.customerModuleAccess)) {
          console.log(`❌ Customer module access denied for: ${item.label} (requires ${item.customerModuleAccess})`)
          return false
        }

        // If no role requirements specified, allow access (for items without any restrictions)
        const hasRoleRequirements = item.requireHRManager || item.requireRCManager || item.requireRCEmployee || item.requireAnyManager || item.requireSuperAdmin || item.requireCoach
        if (!hasRoleRequirements) {
          return true
        }

        if (hasRoleAccess) {
          console.log(`✅ Access granted via role for: ${item.label}`)
          return true
        }

        console.log(`❌ Access denied via role for: ${item.label}`)
        return false
      })

      // Override "Customers" label with custom customer_label from account settings
      const customLabel = account?.customer_label
      const itemsWithCustomLabel = filteredItems.map((item) => {
        if (item.id === 'customers' && customLabel) {
          return { ...item, label: customLabel }
        }
        // Employees should land on org-structure instead of overview
        if (item.id === 'workforce' && isEmployee && !isSuperAdmin && !isHRBP && !isManager) {
          return { ...item, path: '/workforce/org-structure' }
        }
        return item
      })

      return {
        ...section,
        items: itemsWithCustomLabel,
      }
    }).filter((section) => section.items.length > 0) // Remove sections with no items

    // For external collaborators, restrict sidebar to only the Job Builder item
    if (isCollaboratorRoute) {
      console.log('🔧 [COLLABORATOR SIDEBAR] Detected collaborator route, filtering navigation')
      // Prefer the raw navigation config to avoid subscription / role filters
      const compBuilderSection = NAVIGATION_CONFIG.find((section) => section.id === 'comp-builder')
      console.log('🔧 [COLLABORATOR SIDEBAR] Found comp-builder section:', !!compBuilderSection)

      if (compBuilderSection) {
        const jobBldrItem = compBuilderSection.items.find((item) => item.id === 'job-builder')
        console.log('🔧 [COLLABORATOR SIDEBAR] Found job-builder item:', !!jobBldrItem)
        if (jobBldrItem) {
          const collaboratorNav = [
            {
              ...compBuilderSection,
              // Ignore requireHRManager / subscriptions for collaborator context
              items: [jobBldrItem],
            },
          ]
          console.log('🔧 [COLLABORATOR SIDEBAR] Returning collaborator navigation:', collaboratorNav)
          return collaboratorNav
        }
      }

      // Fallback: try to use whatever comp-builder section survived filtering
      const fallbackNav = baseNavigation
        .filter((section) => section.id === 'comp-builder')
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => item.id === 'job-builder'),
        }))
      console.log('🔧 [COLLABORATOR SIDEBAR] Using fallback navigation:', fallbackNav)
      return fallbackNav
    }

    return baseNavigation
  }, [isSubmoduleEnabled, isModuleEnabled, isModuleActive, isSuperAdmin, isHRBP, isManager, isEmployee, hasModuleAccess, hasAccess, isCustomerAdmin, hasCustomerModuleAccess, canManageCustomers, isCoach, isCoachee, marketplaceEnabled, learnerCourseAccess, isLearnSettingsLoaded, isMarketplaceSidebarVisible, isCoursesSidebarVisible, location.pathname, location.search])

  // CRITICAL: Wait for auth, roles, and permissions to load before rendering navigation
  // This prevents rendering sidebar with wrong/default role values.
  // Exception: external collaborators have no Keycloak session, so auth/access/subscription
  // hooks may never resolve. Skip the loading gate for them — their nav is static.
  const isCollaboratorSession = localStorage.getItem('collaborator_mode') === 'true'
  if ((isAuthLoading || isAccessLoading || isSubscriptionLoading) && !isCollaboratorSession) {
    return <SidebarSkeleton isCollapsed={isSidebarCollapsed} />
  }

  return (
    <div
      className="app-sidebar-container flex flex-col h-full border-r z-[60] transition-all duration-300 ease-in-out flex-shrink-0"
      style={{
        width: isSidebarCollapsed ? '64px' : '256px',
        background: 'linear-gradient(180deg, #EEF2FF 0%, #FAF5FF 50%, #FDF2F8 100%)',
        borderColor: '#E5E7EB',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      {/* Header with Logo and Toggle */}
      <div className={cn(
        "flex items-center border-b border-gray-100",
        isSidebarCollapsed ? "justify-center px-2 py-4 flex-col gap-3" : "justify-between px-4 py-6"
      )}>
        <div className={cn(
          "flex items-center min-w-0",
          isSidebarCollapsed ? "justify-center" : "gap-3 flex-1"
        )}>
          <Logo accountName={getAccountInfo()} isAuthenticated={isAuthenticated} isCollapsed={isSidebarCollapsed} />
        </div>

        {/* Collapse Toggle Button */}
        <button
          onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          className="flex items-center justify-center p-1.5 rounded-md transition-all hover:bg-gray-100 active:bg-gray-200 flex-shrink-0"
          style={{
            color: '#6B7280',
          }}
          title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={cn(
              'transition-transform duration-300',
              isSidebarCollapsed && 'rotate-180'
            )}
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <line x1="9" y1="3" x2="9" y2="21" />
          </svg>
        </button>
      </div>

      {/* Dynamic Navigation Sections */}
      <div className="flex-1 overflow-y-auto overflow-x-visible scrollbar-sidebar" style={{ overflowX: 'visible' }}>
        {/* Top-level Navigation Items (not in collapsible sections) */}
        {TOP_LEVEL_NAV_ITEMS.length > 0 && (
          <div style={{
            padding: isSidebarCollapsed ? '0 8px' : '0 16px',
            marginBottom: isSidebarCollapsed ? '8px' : '16px'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {TOP_LEVEL_NAV_ITEMS.map((item) => (
                <NavItem
                  key={item.id}
                  icon={item.icon}
                  label={item.label}
                  path={item.path}
                  activePath={item.activePath}
                  onClick={() => handleNavigate(item.path)}
                  children={item.children}
                  sidebarCollapsed={isSidebarCollapsed}
                  isSuperAdmin={isSuperAdmin}
                />
              ))}
            </div>
          </div>
        )}

        {filteredNavigation.map((section) => (
          <CollapsibleSection
            key={section.id}
            title={section.title}
            isCollapsed={collapsedSections[section.id] ?? section.defaultCollapsed ?? false}
            onToggle={() => toggleSection(section.id)}
            sidebarCollapsed={isSidebarCollapsed}
          >
            {section.items.map((item) => (
              <NavItem
                key={item.id}
                icon={item.icon}
                label={item.label}
                path={item.path}
                activePath={item.activePath}
                onClick={() => handleNavigate(item.path)}
                children={item.children}
                sidebarCollapsed={isSidebarCollapsed}
                isSuperAdmin={isSuperAdmin}
              />
            ))}
          </CollapsibleSection>
        ))}
      </div>

    </div>
  )
}


export default DynamicSidebar
